import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { BugsnagService } from '@cumlaude/bugsnag';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { DataErrorMessageEnum, RestErrorMessageEnum } from '@cumlaude/service-contract';
import { parseError } from '../services/error-util';

/**
 * Zet deze token op de HttpContext van een request om de ServerErrorInterceptor over te slaan.
 * De originele HttpErrorResponse vloeit dan ongewijzigd door naar de aanroeper (geen Bugsnag/toast).
 */
export const SKIP_SERVER_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	protected readonly toastr = inject(ToastrService);
	protected readonly bugsnag = inject(BugsnagService);

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!req.url.startsWith(this.envConfig.dataUrl!)) return next.handle(req);
		if (req.context.get(SKIP_SERVER_ERROR_HANDLING)) return next.handle(req);

		return next.handle(req).pipe(
			catchError((err) => {
				if (!(err instanceof HttpErrorResponse)) return throwError(() => err);

				// afgebroken request (bijv. wegnavigeren): niet melden, maar wel als fout doorgeven (EMPTY laat firstValueFrom met een EmptyError falen)
				if (err.status === 0) return throwError(() => Object.assign(new Error('Request aborted'), { error: RestErrorMessageEnum.ABORTED }));

				const error = err.error;
				if (error instanceof Error) {
					this.bugsnag.notify(error);
					return throwError(() => error);
				}

				const dataErrorMessage = parseError(error);
				switch (dataErrorMessage.error) {
					case DataErrorMessageEnum.INTERRUPTED:
						return EMPTY;
					case DataErrorMessageEnum.QUERY_TIMEOUT:
						this.bugsnag.notify(new Error('Timeout bij het ophalen van data'));
						this.toastr.error('Timeout bij ophalen data');
						return EMPTY;
					case DataErrorMessageEnum.UNKNOWN:
						this.bugsnag.notify(new Error(`Unknown error: ${dataErrorMessage.message}`));
						break;
					case DataErrorMessageEnum.UNEXPECTED:
						this.bugsnag.notify(new Error(`Unexpected error: ${dataErrorMessage.message}`));
						break;
					case DataErrorMessageEnum.MISSING_MODULE:
					case DataErrorMessageEnum.MISSING_ROLE:
					case DataErrorMessageEnum.NO_ACCESS:
						this.bugsnag.notify(new Error(`${dataErrorMessage.error} error occurred.`));
						break;
				}

				const serverError = new Error('Server Error occurred');
				Object.assign(serverError, dataErrorMessage);
				return throwError(() => serverError);
			})
		);
	}
}
